import {
  type SharedExtensionDefinition,
  type SharedExtensionMetaInfo,
  type SharedExtensionMetaInfoArg,
  type SharedExtensionMethod,
  type SharedExtensionMethodParam,
  type SharedExtensionServiceInfo,
} from '@shared/scheme'

import { filesTypeDefs } from './custom-type-defs/files'
import type { Doclet, DocletTag } from './lib'
import { parseJSDoc } from './lib'
import type { TypedefsMap } from './types'

// ============================================================================
// Internal types
// ============================================================================

interface ParsedJSDocsResult {
  serviceInfo: SharedExtensionServiceInfo | null
  methods: MethodInfo[]
  typedefsMap: TypedefsMap
}

interface MethodInfo extends Omit<SharedExtensionMethod, 'bodyParams' | 'queryParams' | 'metaInfo'> {
  // name: string
  // description: string
  // returnType: string
  // registerAs: string
  // colorStart: string
  // colorEnd: string
  private: boolean
  // operationName: string
  // customIcon: string
  // category: string
  sampleResult: SharedExtensionMethod['metaInfo']['sampleResult']
  sampleResultLoader: SharedExtensionMethod['metaInfo']['sampleResultLoader']
  route?: string
  routeStr: string
  paramDefs: SharedExtensionMethod['bodyParams']
}

type TagCallback = (title: string, value: string, tag: DocletTag) => void

// ============================================================================
// customTypeDef — registry of built-in reusable param groups.
// To add a new domain: create custom-type-defs/<domain>.ts and spread it here.
// ============================================================================

const customTypeDef: TypedefsMap = {
  FilesUploadOptions: filesTypeDefs.FilesUploadOptions,
  FilesListOptions: filesTypeDefs.FilesListOptions,
  FilesGetOptions: filesTypeDefs.FilesGetOptions,
  FilesDeleteOptions: filesTypeDefs.FilesDeleteOptions,
}

type TagProcessor<T> = (target: T, tagValue: string) => void

// ============================================================================
// Doclet extraction
// ============================================================================

/**
 * Find the main service class from doclets.
 * Looks for a class with @integrationName tag, or falls back to the last non-Error class.
 */
function findServiceClassName(doclets: Doclet[]): string | null {
  const withIntegration = doclets
    .find(d => {
      if (d.kind === 'class' && d.tags) {
        return d.tags.find(t => t.originalTitle === 'integrationName')
      }
    })

  if (withIntegration) {
    return withIntegration.name
  }

  return null
}

/**
 * Parse a JS file, extract the service class, and return normalized doclets.
 */
function parseJSDocs(filePath: string): ParsedJSDocsResult {
  const doclets = parseJSDoc(filePath)
  const className = findServiceClassName(doclets)

  let serviceInfo: SharedExtensionServiceInfo | null = null

  const methods: MethodInfo[] = []

  const typedefsMap: TypedefsMap = {}

  doclets.forEach(d => {
    if (d.kind === 'class' && d.name === className) {
      serviceInfo = composeServiceInfo(d)
    } else if (d.kind === 'function' && d.memberof === className && d.scope === 'instance') {
      methods.push(composeMethod(d))
    } else if (d.kind === 'typedef') {
      const typedefParams = collectTypedef(d)

      if (typedefParams) {
        typedefsMap[d.name] = typedefParams
      }
    }
  })

  return {
    serviceInfo,
    methods,
    // Extension-defined typedefs override built-ins so authors can customise if needed
    typedefsMap: { ...customTypeDef, ...typedefsMap },
  }
}

const ServiceInfoTagsProcessors: Record<string, TagProcessor<SharedExtensionServiceInfo>> = {
  integrationName: (serviceInfo, tagValue) => {
    serviceInfo.integrationName = tagValue || undefined as unknown as string
  },

  integrationIcon: (serviceInfo, tagValue) => {
    serviceInfo.integrationIcon = tagValue || null
  },

  requireOAuth: serviceInfo => {
    serviceInfo.requireOAuth = true
  },

  integrationTriggersScope: (serviceInfo, tagValue) => {
    serviceInfo.integrationTriggersScope = tagValue || null
  },
}

function composeServiceInfo(d: Doclet): SharedExtensionServiceInfo {
  const serviceInfo: SharedExtensionServiceInfo = {
    serviceClassName: d.name,
    integrationName: d.name,
    integrationIcon: null,
    requireOAuth: undefined,
    integrationTriggersScope: null,
  }

  iterateTags(d.tags, (tagName, tagValue) => {
    if (ServiceInfoTagsProcessors[tagName]) {
      ServiceInfoTagsProcessors[tagName](serviceInfo, tagValue)
    }
  })

  return serviceInfo
}

function iterateTags(tags: DocletTag[] | undefined, cb: TagCallback): void {
  if (tags) {
    tags.forEach(tag => {
      const title = tag.originalTitle
      const value = tag.value || tag.text || ''

      cb(title, value, tag)
    })
  }
}

const MethodTagsProcessors: Record<string, TagProcessor<MethodInfo>> = {
  paramDef: (methodInfo, tagValue) => {
    if (tagValue) {
      const parsedValue = parseJSON(tagValue)

      if (parsedValue) {
        methodInfo.paramDefs.push(parsedValue as SharedExtensionMethodParam)
      }
    }
  },

  route: (methodInfo, tagValue) => {
    if (tagValue) {
      methodInfo.route = tagValue
    }
  },

  registerAs: (methodInfo, tagValue) => {
    if (tagValue) {
      methodInfo.registerAs = tagValue
    }
  },

  operationName: (methodInfo, tagValue) => {
    if (tagValue) {
      methodInfo.operationName = tagValue
    }
  },

  customIcon: (methodInfo, tagValue) => {
    if (tagValue) {
      methodInfo.customIcon = tagValue
    }
  },

  category: (methodInfo, tagValue) => {
    if (tagValue) {
      methodInfo.category = tagValue
    }
  },

  description: (methodInfo, tagValue) => {
    if (tagValue) {
      methodInfo.description = tagValue
    }
  },

  appearanceColor: (methodInfo, tagValue) => {
    if (tagValue) {
      const colors = extractColors(tagValue)

      methodInfo.colorStart = colors.colorStart
      methodInfo.colorEnd = colors.colorEnd
    }
  },

  private: methodInfo => {
    methodInfo.private = true
  },

  sampleResult: (methodInfo, tagValue) => {
    if (tagValue) {
      methodInfo.sampleResult = parseJSON(tagValue)
    }
  },

  sampleResultLoader: (methodInfo, tagValue) => {
    if (tagValue) {
      methodInfo.sampleResultLoader = parseJSON(tagValue) as SharedExtensionMetaInfo['sampleResultLoader']
    }
  },
}

function composeMethod(m: Doclet): MethodInfo {
  const methodInfo: MethodInfo = {
    name: m.name,
    description: m.description || '',
    returnType: mapReturnType(m.returns?.[0].type?.names?.[0]),
    registerAs: '',
    colorStart: '',
    colorEnd: '',
    private: m.access === 'private',
    operationName: m.name,
    customIcon: '',
    category: '',
    sampleResult: null,
    sampleResultLoader: null,
    routeStr: '',
    paramDefs: [],
  }

  if (m.params) {
    m.params.forEach(p => {
      methodInfo.paramDefs.push({
        name: p.name,
        type: p.type?.names?.[0] || 'String',
        required: !p.optional,
      })
    })
  }

  iterateTags(m.tags, (tagName, tagValue) => {
    if (MethodTagsProcessors[tagName]) {
      MethodTagsProcessors[tagName](methodInfo, tagValue)
    }
  })

  return methodInfo
}

// ============================================================================
// Type mapping
// ============================================================================

const PRIMITIVES = ['String', 'Object', 'Boolean', 'Number']

const ParamTypesMap: Record<string, string> = {
  String: 'STRING',
  Object: 'OBJECT',
  Boolean: 'BOOLEAN',
  Number: 'NUMBER',
}

function mapParamType(type: string | undefined): string {
  if (!type) {
    return 'STRING'
  }

  if (type.startsWith('Array')) {
    return 'LIST'
  }

  return ParamTypesMap[type] || 'STRING'
}

function mapReturnType(type: string | undefined): string {
  if (type) {
    if (type === 'Array' || type.startsWith('Array.') || type.startsWith('Array<')) return 'LIST'
    if (type === 'Object') return 'OBJECT'
    if (type === 'String') return 'STRING'
  }

  return 'VOID'
}

// ============================================================================
// Typedef map (from @paramDef tags on typedefs)
// ============================================================================

function collectTypedef(d: Doclet): SharedExtensionMethodParam[] | undefined {
  const defs: SharedExtensionMethodParam[] = []

  iterateTags(d.tags, (title, value) => {
    if (title === 'paramDef') {
      const parsedValue = parseJSON(value)

      if (parsedValue) {
        defs.push(parsedValue as SharedExtensionMethodParam)
      }
    }
  })

  if (d.properties) {
    d.properties.forEach(property => {
      // JSDoc may omit the type on a @property line — preserved as-is at runtime
      const type = property?.type?.names?.[0] as string

      defs.push({
        name: property.name,
        required: !property.optional,
        description: property.description,
        type,
      })
    })
  }

  if (defs.length > 0) {
    return defs
  }
}

// ============================================================================
// Param building (resolves typedefs)
// ============================================================================

function buildParam(paramDef: SharedExtensionMethodParam, typedefsMap: TypedefsMap, nested = false): SharedExtensionMethodParam {
  const isCustomType = !nested &&
    paramDef.type &&
    !PRIMITIVES.includes(paramDef.type) &&
    !paramDef.type.startsWith('Array')

  let properties: SharedExtensionMethodParam[] | undefined

  if (isCustomType && typedefsMap[paramDef.type!]) {
    let subDefs = typedefsMap[paramDef.type!]

    if (paramDef.include?.length) {
      const includeSet = new Set(paramDef.include)

      subDefs = subDefs.filter(sd => includeSet.has(sd.name))
    }

    if (subDefs.length > 0) {
      properties = subDefs.map(sp => buildParam(sp, typedefsMap, true))
    }
  }

  const param: SharedExtensionMethodParam = {
    name: paramDef.name,
    required: paramDef.required || false,
    description: paramDef.description || '',
    type: isCustomType ? 'OBJECT' : mapParamType(paramDef.type),
  }

  if (nested) {
    if (paramDef.label !== undefined) {
      param.label = paramDef.label
    }

    if (paramDef.uiComponent !== undefined) {
      param.uiComponent = paramDef.uiComponent
    }

    if (paramDef.dependsOn !== undefined) {
      param.dependsOn = paramDef.dependsOn
    }

    if (paramDef.dictionary !== undefined) {
      param.dictionary = paramDef.dictionary
    }

    if (paramDef.defaultValue !== undefined) {
      param.defaultValue = paramDef.defaultValue
    }
  }

  if (properties && properties.length > 0) {
    param.properties = properties
  }

  if (param.type === 'LIST') {
    param.items = { type: 'OBJECT' }
  }

  return param
}

// ============================================================================
// MetaInfo args
// ============================================================================

function buildMetaInfoArgs(paramDefs: SharedExtensionMethodParam[], typedefsMap: TypedefsMap): Record<string, SharedExtensionMetaInfoArg> {
  const args: Record<string, SharedExtensionMetaInfoArg> = {}

  for (const pd of paramDefs) {
    const isCustomType = pd.type &&
      !PRIMITIVES.includes(pd.type) &&
      !pd.type.startsWith('Array')

    if (isCustomType && typedefsMap[pd.type!]) {
      const entry: SharedExtensionMetaInfoArg = {}
      if (pd.label) entry.label = pd.label
      args[pd.name] = entry
      continue
    }

    const entry: SharedExtensionMetaInfoArg = {}

    if (pd.label) entry.label = pd.label
    if (pd.dictionary) entry.dictionary = pd.dictionary
    if (pd.dependsOn && pd.dependsOn.length > 0) entry.dependsOn = pd.dependsOn
    if (pd.uiComponent) entry.uiComponent = pd.uiComponent

    args[pd.name] = entry
  }

  return args
}

// ============================================================================
// Colors
// ============================================================================

function extractColors(colorStr: string): { colorStart: string; colorEnd: string } {
  if (!colorStr) {
    return { colorStart: '', colorEnd: '' }
  }

  const colors = colorStr.split(/[,\s]+/).map(c => c.trim()).filter(Boolean)

  return {
    colorStart: colors[0] || '',
    colorEnd: colors[1] || colors[0] || '',
  }
}

// ============================================================================
// Service JSON builder — converts normalized parseJSDocs output to service JSON
// ============================================================================

function parseJSON(str: string): unknown {
  try {
    return JSON.parse(str)
  } catch {
    return null
  }
}

export function parseJsDocToServiceJson(entryPoint: string): SharedExtensionDefinition {
  const { serviceInfo, methods, typedefsMap } = parseJSDocs(entryPoint)

  const methodsMap: SharedExtensionDefinition['methodsMap'] = {}

  for (const method of methods) {
    if (method.private) {
      continue
    }

    const {
      name,
      route,
      registerAs,
      colorStart,
      colorEnd,
      operationName,
      customIcon,
      category,
      description,
      sampleResult,
      sampleResultLoader,
      paramDefs,
      returnType,
    } = method

    const isSystem = registerAs === 'SYSTEM'

    // Determine HTTP method from @route for param placement
    const httpMethod = route
      ? route.split(/\s+/)[0].toUpperCase()
      : name.startsWith('get') ? 'GET' : 'POST'

    const isQueryPlacement = httpMethod === 'GET'

    // Build params
    const params = paramDefs.map(pd => buildParam(pd, typedefsMap))

    const queryParams = isQueryPlacement ? params : []
    const bodyParams = isQueryPlacement ? [] : params

    // Build metaInfo — only from @paramDef, not from standard @param fallback
    const metaInfoArgs = paramDefs.length > 0
      ? buildMetaInfoArgs(paramDefs, typedefsMap)
      : {}

    methodsMap[name] = {
      colorStart,
      colorEnd,
      queryParams, // todo remove me
      bodyParams,
      customIcon: isSystem ? undefined : customIcon,
      registerAs,
      category,
      metaInfo: {
        args: metaInfoArgs,
        sampleResult: isSystem ? undefined : sampleResult,
        sampleResultLoader: (isSystem ? undefined : sampleResultLoader) as SharedExtensionMethod['metaInfo']['sampleResultLoader'],
      },
      returnType,
      description,
      operationName,
      name,
    }
  }

  return {
    runnerVersion: 'v1', //TODO: temp
    methodsMap,
    serviceInfo: serviceInfo!,
  }
}
